import { Controller } from "@hotwired/stimulus"

// Контроллер формы регистрации дропа:
// - Международная маска телефона (авто-определение страны по первым цифрам)
// - Валидация обязательных галочек согласий перед submit
// - Timestamp загрузки формы (антиспам — слишком быстрое заполнение)
//
export default class extends Controller {
  static targets = ["phone", "agreement", "submitBtn", "formLoadedAt"]

  connect() {
    // Записываем время открытия формы (антиспам)
    if (this.hasFormLoadedAtTarget) {
      this.formLoadedAtTarget.value = Math.floor(Date.now() / 1000)
    }

    if (this.hasPhoneTarget) {
      this.formatPhone()
    }
    this.updateSubmitState()
  }

  // === Телефон ===

  formatPhone() {
    if (!this.hasPhoneTarget) return
    const input = this.phoneTarget
    const cursor = input.selectionStart
    const oldLen = input.value.length

    const digits = input.value.replace(/\D/g, "")
    const formatted = this.formatInternationalPhone(digits)

    input.value = formatted
    // Подправляем курсор
    const newLen = formatted.length
    const newPos = Math.max(0, cursor + (newLen - oldLen))
    input.setSelectionRange(newPos, newPos)

    this.validatePhone()
  }

  validatePhone() {
    if (!this.hasPhoneTarget) return
    const digits = this.phoneTarget.value.replace(/\D/g, "")

    if (!digits.length) {
      this.phoneTarget.setCustomValidity("")
      return
    }

    // Минимум 10 цифр (большинство стран), максимум 15 (ITU-T E.164)
    const valid = digits.length >= 10 && digits.length <= 15
    this.phoneTarget.setCustomValidity(valid ? "" : "Введите телефон полностью")
  }

  // Форматирует телефон с автоопределением страны
  formatInternationalPhone(digits) {
    if (!digits.length) return ""

    // Нормализация: 8xxx -> 7xxx (Россия)
    if (digits.startsWith("8") && digits.length >= 2 && digits[1] === "9") {
      digits = "7" + digits.slice(1)
    }

    // Определяем код страны и формат
    const country = this.detectCountry(digits)

    if (country) {
      const code = country.code
      const rest = digits.slice(code.length)
      return "+" + code + " " + this.applyMask(rest, country.mask)
    }

    // Фоллбэк: просто +digits
    return "+" + digits
  }

  detectCountry(digits) {
    // Список популярных стран (код → маска)
    const countries = [
      { code: "7",   mask: "(___) ___-__-__", len: 11 },   // Россия, Казахстан
      { code: "375", mask: "(__) ___-__-__", len: 12 },  // Беларусь
      { code: "380", mask: "(__) ___-__-__", len: 12 },  // Украина
      { code: "998", mask: "(__) ___-__-__", len: 12 },  // Узбекистан
      { code: "996", mask: "(___) __-__-__", len: 12 },  // Кыргызстан
      { code: "992", mask: "(__) ___-__-__", len: 12 },  // Таджикистан
      { code: "993", mask: "(__) __-__-__", len: 11 },   // Туркменистан
      { code: "994", mask: "(__) ___-__-__", len: 12 },  // Азербайджан
      { code: "374", mask: "(__) ___-___", len: 11 },    // Армения
      { code: "995", mask: "(___) __-__-__", len: 12 },  // Грузия
      { code: "373", mask: "(__) ___-___", len: 11 },    // Молдова
      { code: "90",  mask: "(___) ___-__-__", len: 12 }, // Турция
      { code: "971", mask: "(__) ___-____", len: 12 },   // ОАЭ
      { code: "1",   mask: "(___) ___-____", len: 11 },  // США, Канада
      { code: "44",  mask: "(____) ______", len: 12 },   // Великобритания
      { code: "49",  mask: "(____) _______", len: 13 },  // Германия
    ]

    // Сортируем по длине кода DESC (сначала 3-значные, потом 2, потом 1)
    for (const c of countries.sort((a, b) => b.code.length - a.code.length)) {
      if (digits.startsWith(c.code)) return c
    }

    return null
  }

  applyMask(digits, mask) {
    let result = ""
    let di = 0
    for (let i = 0; i < mask.length && di < digits.length; i++) {
      if (mask[i] === "_") {
        result += digits[di++]
      } else {
        result += mask[i]
        // Если следующий символ маски не _ и мы достигли конца цифр — добавляем символ
      }
    }
    // Если остались цифры за пределами маски
    if (di < digits.length) {
      result += digits.slice(di)
    }
    return result
  }

  // === Согласия ===

  checkAgreements() {
    this.updateSubmitState()
  }

  updateSubmitState() {
    if (!this.hasSubmitBtnTarget) return

    const allChecked = this.agreementTargets.every(cb => cb.checked)
    this.submitBtnTarget.disabled = !allChecked

    if (allChecked) {
      this.submitBtnTarget.classList.remove("opacity-50", "cursor-not-allowed")
      this.submitBtnTarget.classList.add("cursor-pointer")
    } else {
      this.submitBtnTarget.classList.add("opacity-50", "cursor-not-allowed")
      this.submitBtnTarget.classList.remove("cursor-pointer")
    }
  }

  // === Submit валидация ===

  validateBeforeSubmit(event) {
    const allChecked = this.agreementTargets.every(cb => cb.checked)
    if (!allChecked) {
      event.preventDefault()
      // Подсветим непроверенные
      this.agreementTargets.forEach(cb => {
        const label = cb.closest("label")
        if (!cb.checked && label) {
          label.classList.add("border-red-400", "bg-red-50")
          setTimeout(() => {
            label.classList.remove("border-red-400", "bg-red-50")
          }, 2000)
        }
      })
    }
  }
}
